import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { tinykeys } from "tinykeys";

import { Button } from "@/components/ui/button";
import { Kbd } from "@/components/ui/kbd";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { isInputFocused } from "@/lib/shortcuts";

interface DetailPageNavProps {
  /** Ordered sibling record ids — typically the list page's row order. */
  ids: readonly string[];
  /** The record currently rendered on this detail page. */
  currentId: string;
  /**
   * Override for the navigation href. Defaults to replacing the last
   * segment of the current router pathname with the target id.
   */
  getHref?: (id: string) => string;
}

/**
 * Linear-style prev/next chevron cluster + "N of M" indicator for v2
 * detail pages. Composes into <DetailPage actions={…}> or any hand-rolled
 * header. Owns its own scoped `j` / `k` window bindings (cleaned on unmount)
 * which respect `isInputFocused`. Renders nothing when there is no usable
 * list context (`ids.length <= 1` or `currentId` not in `ids`).
 */
export function DetailPageNav({ ids, currentId, getHref }: DetailPageNavProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const resolveHref = useCallback(
    (id: string): string =>
      getHref ? getHref(id) : pathname.replace(/\/[^/]+$/, "") + "/" + id,
    [getHref, pathname],
  );
  const currentIndex = ids.indexOf(currentId);
  const isUsable = ids.length > 1 && currentIndex >= 0;
  const prevId = currentIndex > 0 ? ids[currentIndex - 1] : undefined;
  const nextId =
    currentIndex >= 0 && currentIndex < ids.length - 1
      ? ids[currentIndex + 1]
      : undefined;

  // Tracks whether 'g' was recently pressed so j/k don't steal the g+j/g+k
  // global shortcut sequences (e.g. g+j = Jobs navigation).
  const gPressedRef = useRef(false);

  useEffect(() => {
    if (!isUsable) return;
    const onGPress = (e: KeyboardEvent) => {
      if (e.key === "g" && !isInputFocused(e.target)) {
        gPressedRef.current = true;
        setTimeout(() => {
          gPressedRef.current = false;
        }, 1000);
      }
    };
    window.addEventListener("keydown", onGPress, true);
    const unbind = tinykeys(window, {
      j: (e) => {
        if (isInputFocused(e.target) || gPressedRef.current || !nextId) return;
        e.preventDefault();
        navigate(resolveHref(nextId), { state: { ids } });
      },
      k: (e) => {
        if (isInputFocused(e.target) || gPressedRef.current || !prevId) return;
        e.preventDefault();
        navigate(resolveHref(prevId), { state: { ids } });
      },
    });
    return () => {
      window.removeEventListener("keydown", onGPress, true);
      unbind();
    };
  }, [isUsable, prevId, nextId, navigate, resolveHref, ids]);

  if (!isUsable) return null;

  return (
    <div
      className="flex items-center gap-1"
      data-testid="detail-page-nav"
      aria-label="Record navigation"
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={!prevId}
            aria-label="Previous record"
            onClick={() => {
              if (!prevId) return;
              navigate(resolveHref(prevId), { state: { ids } });
            }}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <span className="inline-flex items-center gap-1">
            Previous <Kbd>K</Kbd>
          </span>
        </TooltipContent>
      </Tooltip>
      <span
        className="typo-mono tabular-nums px-2 text-sm text-[color:var(--color-text-lo)]"
        data-testid="detail-page-nav-indicator"
      >
        {currentIndex + 1} of {ids.length}
      </span>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={!nextId}
            aria-label="Next record"
            onClick={() => {
              if (!nextId) return;
              navigate(resolveHref(nextId), { state: { ids } });
            }}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <span className="inline-flex items-center gap-1">
            Next <Kbd>J</Kbd>
          </span>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
